import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { TitleDescriptionInfo } from '@/components/ui/title-description-info';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';

type ModalMode = 'drawer' | 'dialog';

const ModalModeContext = React.createContext<ModalMode>('dialog');

function useModalMode() {
  return React.useContext(ModalModeContext);
}

const Modal = ({
  open,
  onOpenChange,
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) => {
  const isMobile = useIsMobile();
  const mode: ModalMode = isMobile ? 'drawer' : 'dialog';

  if (mode === 'drawer') {
    return (
      <ModalModeContext.Provider value={mode}>
        <Drawer open={open} onOpenChange={onOpenChange} {...props}>
          {children}
        </Drawer>
      </ModalModeContext.Provider>
    );
  }

  return (
    <ModalModeContext.Provider value={mode}>
      <Dialog open={open} onOpenChange={onOpenChange} {...props}>
        {children}
      </Dialog>
    </ModalModeContext.Provider>
  );
};

function splitModalChildren(children: React.ReactNode) {
  const items = React.Children.toArray(children);
  const footerIndex = items.findIndex(
    child =>
      React.isValidElement(child) &&
      (child.type as { displayName?: string })?.displayName === 'ModalFooter'
  );
  if (footerIndex < 0) {
    return { body: children, footer: null };
  }
  return {
    body: [...items.slice(0, footerIndex), ...items.slice(footerIndex + 1)],
    footer: items[footerIndex],
  };
}

const ModalContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const mode = useModalMode();
  const { body, footer } = splitModalChildren(children);

  if (mode === 'drawer') {
    return (
      <DrawerContent
        ref={ref}
        className={cn(
          'flex max-h-[92vh] w-full max-w-[100vw] flex-col overflow-hidden px-0 pb-0',
          className
        )}
        {...props}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-4 pb-2">
          {body}
        </div>
        {footer}
      </DrawerContent>
    );
  }

  return (
    <DialogContent ref={ref} className={className} {...props}>
      {children}
    </DialogContent>
  );
});
ModalContent.displayName = 'ModalContent';

const ModalHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const mode = useModalMode();

  if (mode === 'drawer') {
    return (
      <DrawerHeader
        className={cn('px-0 pb-2 pt-1 text-left', className)}
        {...props}
      />
    );
  }

  return <DialogHeader className={className} {...props} />;
};
ModalHeader.displayName = 'ModalHeader';

export interface ModalTitleProps
  extends React.HTMLAttributes<HTMLHeadingElement> {
  description?: React.ReactNode;
  infoAriaLabel?: string;
}

const ModalTitle = React.forwardRef<HTMLHeadingElement, ModalTitleProps>(
  ({ className, children, description, infoAriaLabel, ...props }, ref) => {
    const mode = useModalMode();

    if (mode === 'drawer') {
      return (
        <div className="flex min-w-0 items-center gap-1.5">
          <DrawerTitle
            ref={ref}
            className={cn(
              'card-title-text min-w-0 font-semibold leading-none tracking-tight',
              className
            )}
            {...props}
          >
            {children}
          </DrawerTitle>
          {description ? (
            <TitleDescriptionInfo
              description={description}
              infoAriaLabel={infoAriaLabel}
              elevated
            />
          ) : null}
        </div>
      );
    }

    return (
      <div className="flex min-w-0 items-center gap-1.5">
        <DialogPrimitive.Title
          ref={ref}
          className={cn(
            'card-title-text min-w-0 font-semibold leading-none tracking-tight',
            className
          )}
          {...props}
        >
          {children}
        </DialogPrimitive.Title>
        {description ? (
          <TitleDescriptionInfo
            description={description}
            infoAriaLabel={infoAriaLabel}
          />
        ) : null}
      </div>
    );
  }
);
ModalTitle.displayName = 'ModalTitle';

const ModalFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const mode = useModalMode();

  if (mode === 'drawer') {
    return (
      <DrawerFooter
        className={cn(
          'mt-0 shrink-0 flex-row justify-end gap-2 border-t border-border/50 px-0 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]',
          className
        )}
        {...props}
      />
    );
  }

  return <DialogFooter className={className} {...props} />;
};
ModalFooter.displayName = 'ModalFooter';

export { Modal, ModalContent, ModalHeader, ModalTitle, ModalFooter };
